import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  getAgentOwnership,
  setAgentEnabled,
  setResourceConnectionEnabled,
  OwnedAgent,
  OwnedResource,
  ApiError,
} from '../lib/api-client';
import { config } from '../config/env';
import { ConfirmDialog } from '../components/ConfirmDialog';

type DisableTarget =
  | { kind: 'agent'; agent: OwnedAgent }
  | { kind: 'resource'; agent: OwnedAgent; resource: OwnedResource };

// Okta's admin console lives on a separate "-admin" subdomain from the
// OIDC issuer used to sign in (e.g. issuer host "demo-teamo.okta.com" ->
// admin console host "demo-teamo-admin.okta.com"). That convention only
// holds for Okta's own domains — a customer signing in through a custom
// domain doesn't get a matching "-admin" host, so we deliberately return
// null (hide the link) rather than construct a guaranteed-wrong URL.
const OKTA_STANDARD_DOMAIN_SUFFIXES = ['.okta.com', '.oktapreview.com', '.okta-emea.com'];

const oktaAddResourceUrl = (oktaAiAgentId: string | null) => {
  if (!config.oktaIssuer || !oktaAiAgentId) return null;
  try {
    const issuerHost = new URL(config.oktaIssuer).hostname;
    const suffix = OKTA_STANDARD_DOMAIN_SUFFIXES.find((s) => issuerHost.endsWith(s));
    if (!suffix) return null;

    const subdomain = issuerHost.slice(0, -suffix.length);
    const adminHost = `${subdomain}-admin${suffix}`;
    return `https://${adminHost}/admin/workload-principals/ai-agents/${oktaAiAgentId}/resource-connections/create`;
  } catch {
    return null;
  }
};

export const AgentOwners = () => {
  const [agents, setAgents] = useState<OwnedAgent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<DisableTarget | null>(null);

  useEffect(() => {
    let cancelled = false;

    getAgentOwnership()
      .then((response) => {
        if (!cancelled) {
          setAgents(response.agents);
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load your agents');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleAgent = async (agent: OwnedAgent) => {
    setPending(agent.agent_id);
    try {
      const result = await setAgentEnabled(agent.agent_id, !agent.enabled);
      setAgents((prev) =>
        prev?.map((a) => (a.agent_id === agent.agent_id ? { ...a, enabled: result.enabled } : a)) ?? prev
      );
    } catch (err) {
      setError((err as ApiError).message || 'Failed to update agent');
    } finally {
      setPending(null);
    }
  };

  // Only disabling needs confirmation — re-enabling is always safe to do
  // immediately. Disabling affects every user assigned to this agent/
  // resource, not just the owner clicking the toggle.
  const handleAgentToggleClick = (agent: OwnedAgent) => {
    if (agent.enabled) {
      setConfirmTarget({ kind: 'agent', agent });
    } else {
      handleToggleAgent(agent);
    }
  };

  const handleResourceToggleClick = (agent: OwnedAgent, resource: OwnedResource) => {
    if (resource.enabled) {
      setConfirmTarget({ kind: 'resource', agent, resource });
    } else {
      handleToggleResource(agent, resource);
    }
  };

  const handleConfirmDisable = () => {
    if (!confirmTarget) return;
    if (confirmTarget.kind === 'agent') {
      handleToggleAgent(confirmTarget.agent);
    } else {
      handleToggleResource(confirmTarget.agent, confirmTarget.resource);
    }
    setConfirmTarget(null);
  };

  const handleToggleResource = async (agent: OwnedAgent, resource: OwnedResource) => {
    const key = `${agent.agent_id}:${resource.connection_id}`;
    setPending(key);
    try {
      const result = await setResourceConnectionEnabled(
        agent.agent_id,
        resource.connection_id,
        !resource.enabled
      );
      setAgents((prev) =>
        prev?.map((a) =>
          a.agent_id !== agent.agent_id
            ? a
            : {
                ...a,
                resources: a.resources.map((r) =>
                  r.connection_id === resource.connection_id ? { ...r, enabled: result.enabled } : r
                ),
              }
        ) ?? prev
      );
    } catch (err) {
      setError((err as ApiError).message || 'Failed to update resource connection');
    } finally {
      setPending(null);
    }
  };

  return (
    <div>
      <h1 className="page-title">Agent Owners</h1>
      {error ? <p className="error">{error}</p> : null}
      {agents === null ? (
        <div className="centered">
          <div className="spinner" />
          <p className="muted">Loading your agents...</p>
        </div>
      ) : agents.length === 0 ? (
        <p className="muted">You don't own any agents.</p>
      ) : (
        <ul className="agent-list">
          {agents.map((agent) => {
            const addResourceUrl = oktaAddResourceUrl(agent.okta_ai_agent_id);
            return (
              <li key={agent.agent_id} className={`agent-card${agent.enabled ? '' : ' disabled'}`}>
                <div className="owner-row">
                  <div>
                    <span className="agent-card-label">Agent</span>
                    <h2 className="agent-card-title">{agent.agent_name}</h2>
                  </div>
                  <div className="owner-actions">
                    <button
                      type="button"
                      className={`toggle-switch agent-toggle${agent.enabled ? ' on' : ''}`}
                      role="switch"
                      aria-checked={agent.enabled}
                      aria-label={`Toggle ${agent.agent_name}`}
                      disabled={pending === agent.agent_id}
                      onClick={() => handleAgentToggleClick(agent)}
                    >
                      <span className="toggle-knob" />
                    </button>
                  </div>
                </div>

                {agent.resources.length > 0 ? (
                  <ul className="resource-list" style={{ marginTop: '0.75rem' }}>
                    {agent.resources.map((resource) => {
                      const key = `${agent.agent_id}:${resource.connection_id}`;
                      return (
                        <li key={resource.connection_id} className="resource-item">
                          <div className="resource-row">
                            <span className="resource-info">
                              <span className={`resource-name${resource.enabled ? '' : ' resource-disabled'}`}>
                                {resource.resource_name}
                              </span>
                              {resource.description ? (
                                <span className="resource-description">{resource.description}</span>
                              ) : null}
                            </span>
                            <div className="owner-actions">
                              <span className={`status-pill${resource.enabled ? ' enabled' : ''}`}>
                                {resource.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                              <button
                                type="button"
                                className={`toggle-switch${resource.enabled ? ' on' : ''}`}
                                role="switch"
                                aria-checked={resource.enabled}
                                aria-label={`Toggle ${resource.resource_name}`}
                                disabled={pending === key}
                                onClick={() => handleResourceToggleClick(agent, resource)}
                              >
                                <span className="toggle-knob" />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="muted" style={{ marginTop: '0.75rem' }}>
                    No resources connected to this agent yet.
                  </p>
                )}

                {addResourceUrl ? (
                  <a className="add-resource-row" href={addResourceUrl} target="_blank" rel="noreferrer">
                    <Plus size={14} />
                    Add resource
                  </a>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {confirmTarget ? (
        <ConfirmDialog
          title={confirmTarget.kind === 'agent' ? 'Disable this agent?' : 'Disable this resource?'}
          message={
            confirmTarget.kind === 'agent'
              ? `Disabling "${confirmTarget.agent.agent_name}" will disable access for every user assigned to this agent, not just you.`
              : `Disabling "${confirmTarget.resource.resource_name}" for "${confirmTarget.agent.agent_name}" will disable access for every user assigned to this agent, not just you.`
          }
          onConfirm={handleConfirmDisable}
          onCancel={() => setConfirmTarget(null)}
        />
      ) : null}
    </div>
  );
};
